import { ArcElement, Chart as ChartJS } from "chart.js";
import { TreemapController, TreemapElement } from "chartjs-chart-treemap";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart, Pie } from "react-chartjs-2";

import { MonthLines, ScrollX } from "./charts";
import {
  baseOptions,
  barDataset,
  CATEGORY_COLORS,
  GENDER_COLORS,
  INK,
  insideLabels,
  MIN_BAR,
  SERIES,
  textOn,
  tooltipStyle,
} from "./chartTheme";
import { Empty } from "./StatCard";

ChartJS.register(ArcElement, TreemapController, TreemapElement);

/* The original Employee Statistics charts, same chart type and same
   click-through as before (each opens the employee list filtered to what was
   clicked, in a new tab), redrawn with the shared chart theme. */

const PLUGINS = [ChartDataLabels];
const FONT = { family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", size: 12 };

const openEmployees = (query) => window.open(`/employees?${query}`, "_blank");

const pointerOnHover = (event, elements) => {
  event.native.target.style.cursor = elements.length ? "pointer" : "default";
};

const clickable = (onPick) => ({
  onHover: pointerOnHover,
  onClick: (event, elements) => {
    if (elements.length) onPick(elements[0]);
  },
});

// ---------------------------------------------------------------- department

// The original chart's floor: every tile at least this "size", so even a
// one-person department is a readable, clickable tile. The label and tooltip
// always show the real count.
const TREEMAP_FLOOR = 70;
const TILE_NAME_MAX = 16;

const shortName = (name) => (name.length > TILE_NAME_MAX ? `${name.slice(0, TILE_NAME_MAX - 1)}…` : name);

/** Treemap, as before: one colour per department, name and count on every
 *  tile. Colours follow the department (name order), not its size. */
export const DepartmentTreemap = ({ departments }) => {
  if (!departments?.length) return <Empty />;
  const tree = departments.map((d, index) => ({
    name: d.name || "No department",
    id: d.id_,
    count: d.count,
    color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
    value: Math.max(d.count, TREEMAP_FLOOR),
  }));
  const tile = (ctx) => ctx.raw?._data || {};
  const data = {
    datasets: [
      {
        tree,
        key: "value",
        spacing: 2,
        borderWidth: 0,
        borderRadius: 6,
        backgroundColor: (ctx) => tile(ctx).color || SERIES.primary,
        labels: {
          display: true,
          overflow: "fit",
          color: (ctx) => textOn(tile(ctx).color || SERIES.primary),
          font: [{ ...FONT, weight: "600" }, { ...FONT, size: 11 }],
          formatter: (ctx) => [shortName(tile(ctx).name || ""), `${tile(ctx).count} employees`],
        },
      },
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        ...tooltipStyle,
        callbacks: {
          title: (items) => items[0]?.raw?._data?.name,
          label: (item) => ` ${item.raw._data.count} employees`,
        },
      },
    },
    ...clickable((el) => {
      const id = el.element.$context.raw._data.id;
      if (id) openEmployees(`department=${id}`);
    }),
  };
  return (
    <div className="ws-chart" style={{ height: 320 }} role="img" aria-label="Employees by department">
      <Chart type="treemap" data={data} options={options} />
    </div>
  );
};

// -------------------------------------------------------------------- gender

/** Pie, as before; "Unknown" is grey rather than a second "Female". */
export const GenderPie = ({ genders }) => {
  const rows = (genders || []).filter((g) => g.count);
  const total = rows.reduce((sum, g) => sum + g.count, 0);
  if (!total) return <Empty />;
  const key = (g) => (g.name || "").toLowerCase();
  const colorOf = (g) => GENDER_COLORS[key(g)] || GENDER_COLORS.other;
  const data = {
    labels: rows.map((g) => g.name),
    datasets: [
      {
        data: rows.map((g) => g.count),
        backgroundColor: rows.map(colorOf),
        borderColor: "#ffffff",
        borderWidth: 2,
        datalabels: {
          color: rows.map((g) => (key(g) === "male" ? "#ffffff" : INK.primary)),
          font: { ...FONT, weight: "700" },
          formatter: (v) => (v / total >= 0.05 ? `${Math.round((v * 100) / total)}%` : ""),
        },
      },
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { color: INK.secondary, font: FONT, usePointStyle: true, pointStyle: "circle", padding: 14 },
      },
      tooltip: {
        ...tooltipStyle,
        callbacks: { label: (item) => ` ${item.label}: ${item.raw} (${Math.round((item.raw * 100) / total)}%)` },
      },
    },
    ...clickable((el) => openEmployees(`gender=${key(rows[el.index])}`)),
  };
  return (
    <div className="ws-chart" style={{ height: 300 }} role="img" aria-label="Gender distribution">
      <Pie data={data} options={options} plugins={PLUGINS} />
    </div>
  );
};

// --------------------------------------------------------------- nationality

/** Same grouping as the old chart: male and female per nationality. */
const groupNationalities = (data) =>
  (data || []).map((item) => {
    const count = (name) => (item.gender || []).filter((g) => g.name === name).reduce((s, g) => s + (g.count || 0), 0);
    return { name: item.nationality || "Unknown", id: item.id_, male: count("male"), female: count("female") };
  });

/** Stacked bars per nationality, male and female, as before. Hovering
 *  anywhere over a column shows both genders; clicking a segment opens that
 *  gender, clicking elsewhere in the column opens the whole nationality. */
export const NationalityBars = ({ data: raw }) => {
  const rows = groupNationalities(raw);
  if (!rows.length) return <Empty />;
  const options = baseOptions({ stacked: true, legend: true });
  options.plugins.tooltip = {
    ...options.plugins.tooltip,
    callbacks: { footer: (items) => `Total: ${rows[items[0].dataIndex].male + rows[items[0].dataIndex].female}` },
  };
  options.onHover = pointerOnHover;
  options.onClick = (event, elements, chart) => {
    if (!elements.length) return;
    const row = rows[elements[0].index];
    if (!row.id) return;
    const [hit] = chart.getElementsAtEventForMode(event, "point", { intersect: true }, false);
    const gender = hit ? ["male", "female"][hit.datasetIndex] : null;
    openEmployees(`nationality=${row.id}${gender ? `&gender=${gender}` : ""}`);
  };
  const segment = (label, values, color, extra) =>
    barDataset(label, values.map((v) => v || null), color, {
      borderSkipped: false,
      borderWidth: { top: 2, bottom: 0, left: 0, right: 0 },
      minBarLength: MIN_BAR,
      datalabels: insideLabels(textOn(color)),
      ...extra,
    });
  const data = {
    labels: rows.map((r) => r.name),
    datasets: [
      segment("Male", rows.map((r) => r.male), GENDER_COLORS.male, { borderRadius: 0 }),
      segment("Female", rows.map((r) => r.female), GENDER_COLORS.female),
    ],
  };
  return (
    <ScrollX count={rows.length} perItem={72} label="Employees by nationality and gender">
      <Chart type="bar" data={data} options={options} plugins={PLUGINS} />
    </ScrollX>
  );
};

// ---------------------------------------------------- designation / cost center

/** Vertical bars with the count on top; `link` builds the employee filter. */
const CategoryBars = ({ rows, label, perItem, link }) => {
  if (!rows?.length) return <Empty />;
  const options = baseOptions();
  options.plugins.datalabels = insideLabels();
  options.scales.x.ticks = { ...options.scales.x.ticks, autoSkip: false, maxRotation: 60, minRotation: 0 };
  Object.assign(options, clickable((el) => {
    const query = link(rows[el.index]);
    if (query) openEmployees(query);
  }));
  const data = {
    labels: rows.map((r) => r.name),
    datasets: [barDataset(label, rows.map((r) => r.count), SERIES.primary, { maxBarThickness: 36, minBarLength: MIN_BAR })],
  };
  return (
    <ScrollX count={rows.length} perItem={perItem} label={label}>
      <Chart type="bar" data={data} options={options} plugins={PLUGINS} />
    </ScrollX>
  );
};

export const DesignationBars = ({ positions }) => (
  <CategoryBars
    rows={positions}
    label="Employees by designation"
    perItem={48}
    link={(p) => (p.id_ ? `position=${p.id_}` : null)}
  />
);

export const CostCenterBars = ({ costCenters }) => (
  <CategoryBars
    rows={(costCenters || []).map((c) => ({ ...c, name: c.name || "Unassigned" }))}
    label="Employees by cost center"
    perItem={56}
    link={(c) => (c.id_ ? `cost_center=${c.id_}` : null)}
  />
);

// ------------------------------------------------------------- new employees

/** Line, as before: joiners per month with the count on each point. */
export const NewEmployeesLine = ({ trend }) => (
  <MonthLines
    trend={trend}
    label="New employees per month"
    series={[{ label: "New Employees", color: SERIES.primary, valueOf: (m) => m.hires }]}
  />
);
